// Implements: F-VER-014, F-UIX-021, F-UIX-009, F-UIX-010
/**
 * The tree of one mockup: its nodes, and which of them is current.
 *
 * Two queries on purpose. The nodes live under `["versions","tree",id]`; the
 * current pointer comes from the mockup itself under the SHARED
 * `["mockups","detail",id]`, the very query the scope selector reads. So the
 * page asks for the mockup once, and whatever refreshes it (a scope change, a
 * finished generation) refreshes what the tree points at. The keys are shared
 * through `shared/api` because a module may not reach into another.
 */

import { useQuery } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey, versionTreeKey } from "../../../shared/api/queryKeys";
import type { MockupDetailView, VersionTreeNode } from "../../../types/api";
import { fetchVersionNodes } from "../api/versionsApi";

export interface VersionTreeData {
  readonly nodes: readonly VersionTreeNode[];
  /** The version the mockup currently points at, or `null` before the first one. */
  readonly currentVersionId: string | null;
}

export type UseVersionTreeResult =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly error: ApiError; readonly refetch: () => void }
  | { readonly status: "success"; readonly data: VersionTreeData };

export function useVersionTree(mockupId: string): UseVersionTreeResult {
  const nodes = useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchVersionNodes(mockupId),
  });
  const mockup = useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });

  const failure = nodes.error ?? mockup.error;
  if (failure !== null) {
    return {
      status: "error",
      error: failure,
      refetch: () => {
        void nodes.refetch();
        void mockup.refetch();
      },
    };
  }
  if (nodes.data === undefined || mockup.data === undefined) return { status: "pending" };
  return {
    status: "success",
    data: { nodes: nodes.data, currentVersionId: mockup.data.current_version_id },
  };
}
